import { fmtUSD } from "@/lib/api";
import { Trophy, ShieldCheck, TrendingDown, Crown } from "lucide-react";

export default function AdminReport({ resumen }) {
  if (!resumen || !resumen.por_vendedor?.length) return null;
  const list = resumen.por_vendedor;
  const porCoord = resumen.por_coordinador || [];

  const topCobrador = [...list].sort((a, b) => b.cobrado_mes - a.cobrado_mes)[0];
  const menosMora = [...list].sort((a, b) => a.valor_vencido - b.valor_vencido)[0];
  const menosPagares = [...list].sort((a, b) => a.pagares_pendientes - b.pagares_pendientes)[0];
  const ranking = [...list].sort((a, b) => b.cobrado_mes - a.cobrado_mes);

  const cards = [
    { icon: Trophy, label: "Quién cobró más en el mes", vend: topCobrador.vendedor, value: fmtUSD(topCobrador.cobrado_mes), accent: "from-amber-400 to-amber-600", testid: "admin-top-cobrador" },
    { icon: ShieldCheck, label: "Menos pendencias en cuentas", vend: menosMora.vendedor, value: `${fmtUSD(menosMora.valor_vencido)} vencido`, accent: "from-emerald-400 to-emerald-600", testid: "admin-menos-mora" },
    { icon: TrendingDown, label: "Mejor recuperación de pagarés", vend: menosPagares.vendedor, value: `${menosPagares.pagares_pendientes} pendientes`, accent: "from-teal-400 to-teal-600", testid: "admin-menos-pagares" },
  ];

  return (
    <section data-testid="admin-report" className="rounded-xl overflow-hidden border border-amber-300 shadow-sm bg-white">
      <div className="px-5 sm:px-6 py-4 border-b border-amber-100 bg-gradient-to-r from-amber-50 to-transparent flex items-center gap-2">
        <Crown className="h-5 w-5 text-amber-600" />
        <div>
          <h3 className="font-heading text-lg font-bold text-amber-900 leading-tight">Informe General de Administración</h3>
          <p className="text-xs text-slate-500">Visión total de la cartera y desempeño comparativo de vendedores · solo administradores</p>
        </div>
      </div>

      <div className="p-4 sm:p-5 space-y-5">
        {porCoord.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-amber-100" data-testid="admin-por-coordinador">
            <div className="px-4 py-2.5 bg-amber-50/60 text-xs font-semibold uppercase tracking-wide text-amber-800">
              Resumen por coordinador · vendedores a cargo
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-3 font-semibold">Coordinador</th>
                  <th className="text-center px-4 py-3 font-semibold">Vendedores</th>
                  <th className="text-center px-4 py-3 font-semibold">Con Informe</th>
                  <th className="text-right px-4 py-3 font-semibold">Total Cartera</th>
                  <th className="text-right px-4 py-3 font-semibold">Vencido</th>
                </tr>
              </thead>
              <tbody>
                {porCoord.map((c) => (
                  <tr key={c.email} data-testid={`admin-coord-${c.email}`} className="border-t border-slate-100 hover:bg-amber-50/40">
                    <td className="px-4 py-2.5 font-semibold text-emerald-900 break-words">{c.coordinador}</td>
                    <td className="px-4 py-2.5 text-center font-mono font-bold text-amber-700 tabular-nums">{c.total_vendedores}</td>
                    <td className="px-4 py-2.5 text-center font-mono text-slate-600 tabular-nums">{c.con_informe}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900 tabular-nums whitespace-nowrap">{fmtUSD(c.total_cartera)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-red-600 tabular-nums whitespace-nowrap">{fmtUSD(c.valor_vencido)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-amber-200 bg-amber-50/50 font-bold">
                  <td className="px-4 py-2.5 text-slate-700">TOTAL · {porCoord.length} coordinadores</td>
                  <td className="px-4 py-2.5 text-center font-mono text-emerald-800 tabular-nums" data-testid="admin-coord-total">{resumen.total_vendedores_coordinados}</td>
                  <td className="px-4 py-2.5 text-center font-mono text-slate-600 tabular-nums">{porCoord.reduce((s, c) => s + c.con_informe, 0)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-900 tabular-nums whitespace-nowrap">{fmtUSD(porCoord.reduce((s, c) => s + c.total_cartera, 0))}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-red-600 tabular-nums whitespace-nowrap">{fmtUSD(porCoord.reduce((s, c) => s + c.valor_vencido, 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.testid} data-testid={c.testid} className="rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className={`h-9 w-9 rounded-lg bg-gradient-to-br ${c.accent} flex items-center justify-center mb-2`}>
                  <Icon className="h-4.5 w-4.5 text-white" />
                </div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{c.label}</div>
                <div className="font-heading font-bold text-emerald-900 text-base sm:text-lg mt-0.5 break-words leading-tight">{c.vend}</div>
                <div className="font-mono text-sm text-slate-600 tabular-nums">{c.value}</div>
              </div>
            );
          })}
        </div>

        <div className="overflow-x-auto rounded-lg border border-slate-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-3 font-semibold">#</th>
                <th className="text-left px-4 py-3 font-semibold">Vendedor</th>
                <th className="text-center px-4 py-3 font-semibold">Clientes</th>
                <th className="text-right px-4 py-3 font-semibold">Total Cartera</th>
                <th className="text-right px-4 py-3 font-semibold">Cobrado del Mes</th>
                <th className="text-right px-4 py-3 font-semibold">Vencido</th>
                <th className="text-center px-4 py-3 font-semibold">Pagarés Pend.</th>
                <th className="text-center px-4 py-3 font-semibold">% Util.</th>
              </tr>
            </thead>
            <tbody>
              {ranking.map((v, i) => (
                <tr key={v.vendedor} className="border-t border-slate-100 hover:bg-amber-50/40">
                  <td className="px-4 py-2.5 font-bold text-amber-600">{i + 1}</td>
                  <td className="px-4 py-2.5 font-semibold text-emerald-900 break-words min-w-[150px] max-w-[240px]">{v.vendedor}</td>
                  <td className="px-4 py-2.5 text-center font-mono text-slate-600 tabular-nums">{v.clientes}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900 tabular-nums whitespace-nowrap">{fmtUSD(v.total_cartera)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-emerald-700 font-semibold tabular-nums whitespace-nowrap">{fmtUSD(v.cobrado_mes)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-red-600 tabular-nums whitespace-nowrap">{fmtUSD(v.valor_vencido)}</td>
                  <td className="px-4 py-2.5 text-center font-mono text-slate-600">{v.pagares_pendientes}</td>
                  <td className="px-4 py-2.5 text-center font-mono text-slate-600">{v.pct_utilizacion}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
